import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { villaAPI } from '../config/api';
import {
  FiUsers, FiHome, FiMapPin, FiChevronLeft, FiChevronDown, FiCheck, FiArrowRight,
  FiPercent, FiDroplet, FiImage, FiStar, FiClock, FiWifi, FiShield, FiCoffee
} from 'react-icons/fi';
import PictureImage from '../components/PictureImage';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';
import { VILLA_PERFECT_FOR, landingLabel } from '../data/seoLandingPages';
import { villa3bhkFAQs, villa4bhkFAQs, villa5bhkFAQs, villa7bhkFAQs, villa8bhkFAQs, villa9bhkFAQs, villa12bhkGeneralFAQs } from '../data/faqs';
import { useSettings } from '../context/SettingsContext';
import { withBhk } from '../utils/villaName';
import { BRAND_BASELINE_RATING, BRAND_BASELINE_COUNT } from '../data/reviews';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = ['Overview', 'Highlights', 'Amenities', 'Refund Policy', 'Location'];

// High-intent SEO metadata per villa — targeting exact-match long-tail keyword
// clusters (BHK + capacity + "private swimming pool Lonavala on rent") plus the
// Dedicated Cooking Space differentiator for AI-search engines.
const VILLA_SEO = {
  '3bhk-garden-escape-lonavala': {
    title: '3BHK Villa in Lonavala with Private Swimming Pool — 5–8 People, From ₹{price}/night | Royal Garden Villas',
    description: '3BHK villa in Lonavala with private swimming pool from ₹{price}/night for 6–8 guests. 3 AC bedrooms + 3 washrooms, baby pool, lawn, swing, sound system, 50 Mbps Wi-Fi — a 3 bedroom villa with swimming pool for 5 to 6 people, families & friends. BYOB, Swiggy/Zomato allowed. Book direct.',
  },
  '4bhk-garden-retreat-lonavala': {
    title: '4BHK Villa with Private Swimming Pool in Lonavala for 8–10 People — From ₹{price}/night | Royal Garden Villas',
    description: '4BHK villa with swimming pool in Lonavala for 8 to 10 people from ₹{price}/night — ideal for family weekends and friends getaways. Private pool with shallow kids zone, lawn, group swing, indoor games, 50 Mbps Wi-Fi, BYOB, Swiggy/Zomato allowed. Instant online booking.',
  },
  '5bhk-garden-delight-lonavala': {
    title: '5BHK Villa with Private Swimming Pool in Lonavala for 10–15 People — From ₹{price}/night | Royal Garden Villas',
    description: '5BHK / 5 room villa with swimming pool in Lonavala for 10 to 15 people from ₹{price}/night — for big families, birthdays, bachelor parties and Gen Z group trips. Largest private pool, hot tub, BBQ area, 50 Mbps Wi-Fi, BYOB, Swiggy/Zomato allowed. Book direct.',
  },
  '7bhk-harmony-villa-lonavala': {
    title: '7BHK Villa with Private Swimming Pool in Lonavala for 15–18 People — From ₹{price}/night | Royal Garden Villas',
    description: '7BHK villa with swimming pool in Lonavala for 15 to 18 people from ₹{price}/night. 7BHK villa with private swimming pool in Lonavala on rent — a combo villa for 14 person, villa for 16 person or villa for 18 persons stays. 2 private pools, 2 lawns, 2 kitchens, optional Dedicated Cooking Space for your own chef / maharaj. Ideal for mid-size family reunions, corporate offsites &amp; birthdays.',
  },
  '8bhk-grandeur-villa-lonavala': {
    title: '8BHK Villa with Private Swimming Pool in Lonavala for 16–20 People — From ₹{price}/night | Royal Garden Villas',
    description: '8BHK villa with swimming pool in Lonavala for 15 to 20 people from ₹{price}/night. 8BHK villa with private swimming pool in Lonavala on rent — villa for 16 person or villa for 18 persons groups. 2 private pools, baby pool, hot tub, BBQ area, 2 kitchens, optional Dedicated Cooking Space with your own maharaj. 90 min from Pune, 2 hrs from Mumbai. Instant online booking.',
  },
  '9bhk-paradise-villa-lonavala': {
    title: '9BHK Villa with Private Swimming Pool in Lonavala for 20–22 People — From ₹{price}/night | Royal Garden Villas',
    description: '9BHK villa with swimming pool in Lonavala for 20 to 22 people from ₹{price}/night. 9BHK villa with private swimming pool in Lonavala on rent — villa for 18 persons to 22-guest stays. 2 private pools, group swing, hot tub, BBQ area, 2 kitchens, optional Dedicated Cooking Space (bring your own chef / maharaj). Perfect for large family reunions, weddings &amp; corporate offsites.',
  },
  '12bhk-celebration-villa-lonavala': {
    title: '12BHK Villa with Private Swimming Pool in Lonavala for 25–35 People — From ₹{price}/night | Royal Garden Villas',
    description: '12BHK big villa in Lonavala for large groups of 25 to 35 people from ₹{price}/night — ideal for 30–35 people corporate offsite trips. 12BHK villa with private swimming pool in Lonavala on rent — villa for 24 persons / villa for 25 persons with scale up to 35 guests. 3 private pools, 3 lawns, 3 kitchens, optional Dedicated Cooking Space with your own maharaj / chef. Perfect for weddings, corporate offsites of up to 35 people &amp; large family celebrations.',
  },
};

// Capacity label used in H2 and intro paragraph — mirrors the exact long-tail
// phrases users search (e.g. "villa for 6 person with private swimming pool…")
const CAPACITY_PHRASE = {
  '3bhk-garden-escape-lonavala': 'villa for 6 person or villa for 8 person',
  '4bhk-garden-retreat-lonavala': 'villa for 8 person or villa for 10 person',
  '5bhk-garden-delight-lonavala': 'villa for 10 person groups (up to 15 guests)',
  '7bhk-harmony-villa-lonavala': 'villa for 14 person, villa for 16 person or villa for 18 persons',
  '8bhk-grandeur-villa-lonavala': 'villa for 16 person or villa for 18 persons',
  '9bhk-paradise-villa-lonavala': 'villa for 18 persons (and up to 22 guests)',
  '12bhk-celebration-villa-lonavala': 'villa for 24 persons / villa for 25 persons (up to 35 guests) and corporate offsite trip for up to 35 persons',
};

const VillaDetail = () => {
  const { id } = useParams();
  const [villa, setVilla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [allVillas, setAllVillas] = useState([]);
  const [cookingEnabled, setCookingEnabled] = useState(true);
  const [cookingPricingMap, setCookingPricingMap] = useState({});
  const { nightDiscountPct, nightDiscountEnabled } = useSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([villaAPI.getById(id), villaAPI.getAll()])
      .then(([villaRes, allRes]) => {
        setVilla(villaRes.data);
        setAllVillas(allRes.data.filter(v => v.id !== id));
      })
      .catch(err => console.error('Error:', err))
      .finally(() => setLoading(false));
    // Feature flag + per-villa cooking price (public endpoint)
    axios.get(`${API}/api/features/combos-and-cooking`)
      .then(r => setCookingEnabled(!!r.data?.cooking_amenity_enabled))
      .catch(() => {});
    axios.get(`${API}/api/features/cooking-amenity`)
      .then(r => setCookingPricingMap(r.data?.pricing || {}))
      .catch(() => {});
  }, [id]);

  // Inject LodgingBusiness + BreadcrumbList JSON-LD + canonical link directly
  // into <head> via useEffect. This bypasses react-helmet-async's inline-script
  // rendering which briefly leaked JSON text into the DOM on hydration.
  useEffect(() => {
    if (!villa || !villa.id) return undefined;
    const capacityPhrase = CAPACITY_PHRASE[villa.id] || `villa for ${villa.base_capacity} persons`;
    const seoDesc = `${villa.bedrooms}BHK villa with private swimming pool in Lonavala on rent — the ${withBhk(villa)} is a ${capacityPhrase} stay. ${villa.description} Optional Dedicated Cooking Space available for bringing your own chef / maharaj.`;
    const amenityList = [
      ...(villa.amenities || []),
      'Dedicated Cooking Space (Optional add-on — bring your own chef / maharaj)',
    ];
    const lodgingSchema = {
      '@context': 'https://schema.org',
      '@type': 'LodgingBusiness',
      '@id': `https://royalgardenvillas.in/villas/${villa.id}`,
      name: withBhk(villa),
      description: seoDesc,
      image: (villa.images && villa.images.length) ? villa.images.slice(0, 6).map(im => im.startsWith('http') ? im : `https://royalgardenvillas.in${im}`) : [],
      telephone: '+91 98200 88292',
      priceRange: `Rs.${(villa.base_price || 0).toLocaleString()} - Rs.${(villa.weekend_price || 0).toLocaleString()} per night`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Tata Dam Road, Tungarli',
        addressLocality: 'Lonavala',
        addressRegion: 'Maharashtra',
        postalCode: '410406',
        addressCountry: 'IN',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 18.764728, longitude: 73.435652 },
      numberOfRooms: villa.bedrooms,
      maximumAttendeeCapacity: villa.capacity,
      occupancy: { '@type': 'QuantitativeValue', minValue: villa.base_capacity, maxValue: villa.capacity, unitText: 'guests' },
      petsAllowed: false,
      amenityFeature: amenityList.map(a => ({ '@type': 'LocationFeatureSpecification', name: a, value: true })),
      starRating: { '@type': 'Rating', ratingValue: BRAND_BASELINE_RATING.toString() },
      aggregateRating: { '@type': 'AggregateRating', ratingValue: BRAND_BASELINE_RATING.toString(), reviewCount: BRAND_BASELINE_COUNT.toString(), bestRating: '5', worstRating: '1' },
      makesOffer: [
        {
          '@type': 'Offer',
          name: `${villa.bedrooms}BHK villa with private swimming pool in Lonavala on rent — weekday rate`,
          price: villa.base_price,
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          validFrom: new Date().toISOString().split('T')[0],
          category: 'Weekday Nightly Rate',
        },
        {
          '@type': 'Offer',
          name: `${villa.bedrooms}BHK villa with private swimming pool in Lonavala on rent — weekend rate`,
          price: villa.weekend_price,
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          validFrom: new Date().toISOString().split('T')[0],
          category: 'Weekend Nightly Rate',
        },
      ],
    };
    // Speakable schema — tells voice assistants (Google Assistant, Alexa) and AI
    // answer engines (Perplexity, Google SGE, ChatGPT Search) which blocks to
    // read aloud / quote verbatim.
    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: ((VILLA_SEO[villa.id] && VILLA_SEO[villa.id].title) || withBhk(villa)).replace('{price}', (villa.base_price || 0).toLocaleString('en-IN')),
      description: seoDesc,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['[data-speakable]'],
      },
      url: `https://royalgardenvillas.in/villas/${villa.id}`,
    };
    const breadcrumbSchema = null; // breadcrumb now rendered by <SEO breadcrumbs=…>
    void breadcrumbSchema;
    const el1 = document.createElement('script');
    el1.type = 'application/ld+json';
    el1.setAttribute('data-testid', 'villa-lodging-schema');
    el1.text = JSON.stringify(lodgingSchema).replace(/</g, '\\u003c');
    const el3 = document.createElement('script');
    el3.type = 'application/ld+json';
    el3.setAttribute('data-testid', 'villa-speakable-schema');
    el3.text = JSON.stringify(speakableSchema).replace(/</g, '\\u003c');
    document.head.appendChild(el1);
    document.head.appendChild(el3);
    return () => {
      [el1, el3].forEach(n => n.parentNode && n.parentNode.removeChild(n));
    };
  }, [villa]);

  if (loading) return <div className="pt-28 min-h-screen flex items-center justify-center bg-[#F7F8FA]"><p className="text-[#6B7280]">Loading...</p></div>;
  if (!villa) return <div className="pt-28 min-h-screen flex items-center justify-center bg-[#F7F8FA]"><p className="text-[#6B7280] mb-4">Villa not found</p><Link to="/villas" className="text-[#F57C20] font-semibold">Back to Villas</Link></div>;

  const halfPrice = Math.round(villa.base_price / 2);
  const images = villa.images || [];
  const seoRaw = VILLA_SEO[villa.id] || {};
  const fromPrice = (villa.base_price || 0).toLocaleString('en-IN');
  const seoMeta = {
    title: seoRaw.title && seoRaw.title.replace('{price}', fromPrice),
    description: seoRaw.description && (seoRaw.description.replace('{price}', fromPrice) + (nightDiscountEnabled ? ` ${nightDiscountPct}% off from the 2nd night.` : '')),
  };

  return (
    <div data-testid="villa-detail-page" className="pt-28 bg-[#F7F8FA]">
      <SEO
        title={seoMeta.title}
        description={seoMeta.description}
        path={`/villas/${villa.id}`}
        image={images[0]?.startsWith('http') ? images[0] : `https://royalgardenvillas.in${images[0] || ''}`}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Villas', url: '/villas' },
          { name: withBhk(villa), url: `/villas/${villa.id}` },
        ]}
      />
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <Link to="/" className="hover:text-[#F57C20] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/villas" className="hover:text-[#F57C20] transition-colors">Villas</Link>
          <span>/</span>
          <span className="text-[#1A1A2E] font-medium">{withBhk(villa)}</span>
        </div>
      </div>

      {/* ===== IMAGE GALLERY (StayVista L-shape layout) ===== */}
      <section data-testid="villa-gallery" className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 rounded-2xl overflow-hidden relative">
          {/* Main Large Image */}
          <div className="md:col-span-2 relative aspect-[16/10] md:aspect-auto md:h-[420px] overflow-hidden cursor-pointer" onClick={() => setShowAllPhotos(true)}>
            <PictureImage src={images[0]} alt={`${villa.bedrooms}BHK villa with private swimming pool in Lonavala — ${villa.name}`} className="w-full h-full object-cover"
              width={1600} height={1000} loading="eager" fetchpriority="high" />
            {nightDiscountEnabled && (
              <div className="absolute top-4 left-4 bg-[#F57C20] text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                <FiPercent className="w-3.5 h-3.5" /> {nightDiscountPct}% OFF 2nd Night+
              </div>
            )}
          </div>
          {/* Side Thumbnails */}
          <div className="hidden md:grid grid-rows-2 gap-3 h-[420px]">
            {images.slice(1, 3).map((img, i) => (
              <div key={i} className="relative overflow-hidden cursor-pointer" onClick={() => setShowAllPhotos(true)}>
                <PictureImage src={img} alt={`${villa.name} — ${villa.bedrooms}BHK private pool villa Lonavala photo ${i + 2}`} className="w-full h-full object-cover"
                  width={800} height={500} />
              </div>
            ))}
          </div>
          {/* View Photos Button */}
          <button
            onClick={() => setShowAllPhotos(true)}
            data-testid="view-all-photos-btn"
            className="absolute bottom-4 right-4 bg-white text-[#1A1A2E] px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-lg hover:bg-[#F57C20] hover:text-white transition-all"
          >
            <FiImage className="w-4 h-4" /> View All Photos ({images.length})
          </button>
        </div>
      </section>

      {/* ===== TITLE + Quick Info ===== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              {villa.is_combined && (
                <span className="bg-[#F57C20]/10 text-[#F57C20] text-xs font-semibold px-3 py-1 rounded-full border border-[#F57C20]/20">
                  {(villa.component_villas?.length || 2) >= 3 ? 'All 3 Villas Combined' : `${villa.component_villas?.length || 2} Villas Combined`}
                </span>
              )}
              <span className="text-[#F57C20] text-xs font-semibold tracking-wider uppercase">{villa.bedrooms}BHK &middot; Lonavala</span>
            </div>
            <h1 data-testid="villa-name" className="text-3xl sm:text-4xl text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {withBhk(villa)}
            </h1>
            {villa.is_combined && (() => {
              const n = villa.component_villas?.length || 2;
              return (
                <p data-testid="celebration-subheadline" className="text-[#374151] text-sm sm:text-base leading-relaxed mb-3 -mt-1 max-w-3xl">
                  {n >= 3 ? (
                    <>
                      <span className="font-semibold text-[#F57C20]">3 Private Villas, 1 Unmatched Compound.</span>{' '}
                      The scale of a {villa.bedrooms}BHK with the privacy, convenience &amp; luxury of three independent villas next door to each other &mdash; each with its own pool, lawn, kitchen, group swing &amp; games sets. Ideal for multi-generational families and corporate retreats.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-[#F57C20]">2 Private Villas, 1 Shared Compound.</span>{' '}
                      The scale of a {villa.bedrooms}BHK across two independent villas next door to each other &mdash; each with its own pool, lawn &amp; kitchen. Ideal for larger groups who want privacy within a single stay.
                    </>
                  )}
                </p>
              );
            })()}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[#6B7280] text-sm">
              <span className="flex items-start gap-1.5" data-testid="villa-guest-capacity">
                <FiUsers className="w-4 h-4 text-[#F57C20] mt-0.5 flex-shrink-0" />
                <span>
                  Villa rental for {villa.base_capacity} guests.
                  <span className="block text-xs text-[#9CA3AF]">
                    Additional guests over {villa.base_capacity} persons shall be Rs.{(villa.extra_guest_charge || 1000).toLocaleString()} / person / single night stay.
                  </span>
                </span>
              </span>
              <span className="flex items-center gap-1.5"><FiHome className="w-4 h-4 text-[#F57C20]" /> {villa.bedrooms} Bedrooms</span>
              <span className="flex items-center gap-1.5"><FiShield className="w-4 h-4 text-[#F57C20]" /> {villa.bedrooms} Washrooms</span>
              <span className="flex items-center gap-1.5"><FiDroplet className="w-4 h-4 text-[#F57C20]" /> Private Pool</span>
              <span className="flex items-center gap-1.5"><FiMapPin className="w-4 h-4 text-[#F57C20]" /> Lonavala, Maharashtra</span>
            </div>
          </div>
          {/* Price CTA (mobile sticky bar handled below) */}
          <div className="hidden lg:block">
            <div className="text-right">
              <p className="text-[#6B7280] text-xs mb-1">Starting from</p>
              <p data-testid="villa-from-price" className="text-3xl font-bold text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Rs.{villa.base_price?.toLocaleString()}<span className="text-sm font-normal text-[#6B7280]"> /night (weekday)</span></p>
              <p data-testid="villa-weekend-price" className="text-xs text-[#6B7280] mt-1">Weekend (Fri–Sun): Rs.{villa.weekend_price?.toLocaleString()} /night</p>
              <Link to={`/booking?villa=${villa.id}`} data-testid="villa-book-now-btn" className="mt-3 inline-block bg-[#F57C20] text-white px-8 py-3 text-sm font-semibold rounded-lg hover:bg-[#E06A10] transition-all hover:shadow-lg hover:shadow-orange-500/20">
                Book This Villa
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TAB NAVIGATION (StayVista-style) ===== */}
      <section data-testid="villa-tabs" className="sticky top-[116px] z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-0 overflow-x-auto scrollbar-hide">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                data-testid={`tab-${tab.toLowerCase().replace(' ', '-')}`}
                className={`px-5 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-[#F57C20] text-[#F57C20]'
                    : 'border-transparent text-[#6B7280] hover:text-[#1A1A2E]'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TAB CONTENT ===== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Overview */}
            {activeTab === 'Overview' && (
              <div data-testid="tab-overview-content">
                <h2 className="text-2xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>About This Villa</h2>
                {/* ===== SEO-RICH INTRO (bakes exact-match long-tail keywords + marked speakable for AI voice/answer engines) ===== */}
                <div data-testid="villa-seo-intro" data-speakable="true" className="bg-white border border-[#F57C20]/15 rounded-xl p-5 mb-6">
                  <p className="text-[#374151] text-sm md:text-[15px] leading-relaxed">
                    <strong className="text-[#1A1A2E]">{villa.bedrooms}BHK villa with private swimming pool in Lonavala on rent</strong> — the {withBhk(villa)} is a {CAPACITY_PHRASE[villa.id] || `villa for ${villa.base_capacity} persons`} stay inside our gated luxury compound on Tata Dam Road, Lonavala. Each booking is <strong>exclusive to your group</strong> — your own private pool{villa.is_combined ? 's' : ''}, private lawn{villa.is_combined ? 's' : ''}, {villa.bedrooms} AC bedrooms, <strong>complimentary 50 Mbps Microscan fiber Wi-Fi</strong>, housekeeping (10 AM – 6 PM) and 24/7 security.
                  </p>
                  {cookingEnabled && (
                    <p className="text-[#374151] text-sm md:text-[15px] leading-relaxed mt-3">
                      <span className="inline-flex items-center gap-1.5 text-[#F57C20] font-semibold"><FiCoffee className="w-4 h-4" /> NEW · Dedicated Cooking Space</span> — the only {villa.bedrooms}BHK villa in Lonavala that lets you <strong>bring your own chef / maharaj</strong> and cook home-style meals in a dedicated outdoor cooking space with gas, utensils, steel platform &amp; washing sink. A unique differentiator you will not find at Airbnb or standard private pool villas in Lonavala.
                    </p>
                  )}
                  <p className="text-[#6B7280] text-xs md:text-[13px] leading-relaxed mt-3">
                    2 hours from Mumbai via the Mumbai-Pune Expressway, 90 minutes from Pune. Instant online booking with Razorpay.{nightDiscountEnabled ? ` ${nightDiscountPct}% off from the 2nd night onwards auto-applied.` : ''}
                  </p>
                </div>

                {/* ===== QUICK FACTS — concise, quotable block for AI engines (Perplexity, ChatGPT, Google SGE, Gemini) ===== */}
                <div data-testid="villa-quick-facts" data-speakable="true" className="bg-gradient-to-br from-[#F7F8FA] to-white border border-gray-200 rounded-xl p-5 mb-6">
                  <h3 className="text-[#1A1A2E] text-sm font-bold tracking-wider uppercase mb-3" style={{ letterSpacing: '0.1em' }}>Quick Facts</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[#374151] text-sm">
                    <li><strong>Villa Type:</strong> {villa.bedrooms}BHK private pool villa in Lonavala on rent</li>
                    <li><strong>Guest Capacity:</strong> Base {villa.base_capacity} / Max {villa.capacity} guests</li>
                    <li><strong>Weekday Rate:</strong> Rs.{(villa.base_price || 0).toLocaleString()} per night</li>
                    <li><strong>Weekend Rate:</strong> Rs.{(villa.weekend_price || 0).toLocaleString()} per night</li>
                    <li><strong>Private Pool{villa.is_combined ? 's' : ''}:</strong> Yes — exclusive to your group</li>
                    <li><strong>Wi-Fi:</strong> Complimentary 50 Mbps Microscan fiber</li>
                    <li><strong>AC Bedrooms:</strong> {villa.bedrooms} with en-suite washrooms</li>
                    <li><strong>Housekeeping:</strong> Available 10 AM – 6 PM</li>
                    <li><strong>Security:</strong> 24/7 security available</li>
                    <li><strong>Location:</strong> Tata Dam Road, Tungarli, Lonavala 410406</li>
                    <li><strong>Distance:</strong> 2 hrs from Mumbai / 90 min from Pune</li>
                    {nightDiscountEnabled && <li><strong>Discount:</strong> {nightDiscountPct}% off from 2nd night (Stay Longer, Pay Less)</li>}
                    <li><strong>Dedicated Cooking Space:</strong> Optional (bring your own chef / maharaj)</li>
                  </ul>
                </div>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-6">{villa.description}</p>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-4">
                  {villa.name} is a premium {villa.bedrooms}-bedroom luxury villa in Lonavala with a private swimming pool,
                  manicured gardens, AC rooms, modern kitchen, complimentary WiFi, parking, and indoor games. Base capacity: {villa.base_capacity} guests (including kids).
                  {villa.is_combined && (villa.component_villas?.length >= 3)
                    ? ' As the Celebration Villa, it combines all 3 properties with 3 private pools, 3 kitchens, and expansive shared spaces.'
                    : villa.is_combined
                      ? ` It combines ${villa.component_villas?.length || 2} independent villas side-by-side with ${villa.component_villas?.length || 2} private pools, ${villa.component_villas?.length || 2} kitchens and separate gardens.`
                      : ''}
                </p>

                {/* AC Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
                  <p className="text-[#1A1A2E] text-sm font-semibold mb-1">Split AC in All Rooms</p>
                  <p className="text-[#6B7280] text-xs leading-relaxed">
                    Split ACs in all {villa.bedrooms} bedrooms and living area. Due to double-height living areas, additional wall and tower fans have been provided for extra cool air support alongside the ACs.
                  </p>
                </div>

                {/* Discount Banner */}
                {nightDiscountEnabled && <div data-testid="villa-discount-banner" className="bg-[#F57C20]/5 border border-[#F57C20]/20 p-5 mb-8 flex items-start gap-4 rounded-xl">
                  <div className="w-14 h-14 flex items-center justify-center border-2 border-[#F57C20] text-[#F57C20] flex-shrink-0 rounded-lg">
                    <span className="text-xl font-bold">{nightDiscountPct}%</span>
                  </div>
                  <div>
                    <p className="text-[#1A1A2E] text-sm font-semibold mb-1">Stay Longer, Pay Less: {nightDiscountPct}% OFF 2nd Night Onwards!</p>
                    <p className="text-[#6B7280] text-xs">
                      1st night at actual rate. From the <span className="text-[#F57C20] font-semibold">2nd night onwards, every additional night gets {nightDiscountPct}% off</span>.
                    </p>
                  </div>
                </div>}

                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                  {[
                    { icon: FiHome, title: `${villa.bedrooms} Bedrooms`, sub: `${villa.bathrooms} Bathrooms` },
                    { icon: FiUsers, title: `${villa.base_capacity} Guests`, sub: `+ Rs.${(villa.extra_guest_charge || 1000).toLocaleString()}/- per additional guest over base ${villa.base_capacity}` },
                    { icon: FiDroplet, title: "Private Pool", sub: villa.is_combined ? `${villa.component_villas?.length || 2} Pools` : "1 Pool" },
                    { icon: FiStar, title: "Extra Butler Available", sub: "(Optional)" },
                  ].map((item, i) => (
                    <div key={i} className="bg-white p-4 text-center rounded-xl border border-gray-100">
                      <item.icon className="w-5 h-5 text-[#F57C20] mx-auto mb-2" />
                      <p className="text-[#1A1A2E] text-sm font-semibold">{item.title}</p>
                      <p className="text-[#6B7280] text-xs">{item.sub}</p>
                    </div>
                  ))}
                </div>

                {/* ===== CELEBRATION VILLA — CORPORATE / LARGE-GROUP DIFFERENTIATORS ===== */}
                {villa.is_combined && (villa.component_villas?.length || 0) >= 3 && (
                  <div data-testid="celebration-compound-section" className="mb-10">
                    <h3 className="text-xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Why Book the Celebration Compound Over a Standard {villa.bedrooms}BHK?
                    </h3>
                    <p className="text-[#6B7280] text-sm leading-relaxed mb-5">
                      Most {villa.bedrooms}BHKs are a single monolithic building with one pool, one kitchen and one lawn shared by 30&ndash;35 guests. The Celebration Compound is three independent villas (3BHK + 4BHK + 5BHK) inside the same private property boundary &mdash; giving your group the flexibility of separate quarters while still enjoying the compound together.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                      {[
                        {
                          icon: FiDroplet,
                          title: 'Tailored Privacy',
                          desc: 'Designate one pool as a ladies-only lounge, one as a kids splash zone, and the third as the main party pool. Separate branches of a family or corporate team can retire to their own villa at night while still meeting on the shared compound by day.'
                        },
                        {
                          icon: FiHome,
                          title: 'Zero Bottlenecks — 3× Everything',
                          desc: '3 swimming pools, 3 private lawns, 3 group swings, 3 carrom & board-game sets, and 3 fully-equipped kitchens for serving. Simultaneous breakfast for 30+ guests, or a separate Jain / Pure-Veg meal serving line in one kitchen while the other two run non-veg service — no queues, no clashes.'
                        },
                        {
                          icon: FiStar,
                          title: 'Best of Both Worlds',
                          desc: 'Expansive shared compound grounds and pool decks for team-building days, welcome dinners and evening celebrations, paired with quiet independent villas when senior team members or families with infants need downtime.'
                        }
                      ].map((p, i) => (
                        <div key={i} className="bg-white border border-gray-100 rounded-xl p-5">
                          <div className="w-10 h-10 rounded-lg bg-[#F57C20]/10 flex items-center justify-center mb-3">
                            <p.icon className="w-5 h-5 text-[#F57C20]" />
                          </div>
                          <p className="text-[#1A1A2E] text-sm font-semibold mb-2">{p.title}</p>
                          <p className="text-[#6B7280] text-xs leading-relaxed">{p.desc}</p>
                        </div>
                      ))}
                    </div>

                    {/* ===== COMPARISON TABLE ===== */}
                    <h4 className="text-lg text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Celebration Villa vs. a Standard 12BHK
                    </h4>
                    <div data-testid="celebration-comparison" className="overflow-x-auto mb-8">
                      <table className="min-w-full text-sm border border-gray-100 rounded-xl overflow-hidden">
                        <thead className="bg-[#1A1A2E] text-white">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider">Feature / Utility</th>
                            <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-[#F57C20]">Celebration Villa</th>
                            <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider">Standard Single 12BHK</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {[
                            ['Swimming Pools', '3 Separate Pools — allows ladies-only, kids-only or main-party pool designations', '1 Shared Pool, crowded for 30+ guests'],
                            ['Outdoor Lawns & Swings', '3 Private Lawns with 3 group swings', '1 Main Lawn'],
                            ['Kitchens & Dining', '3 Independent Kitchens & crockery sets — great for separate Jain/Pure-Veg meal serving alongside non-veg service', '1 Kitchen — bottleneck during meal times'],
                            ['Indoor Games & Lounges', '3 Sets of carrom & board games across 3 lounge areas', '1 Set / Single common living room'],
                            ['Group Privacy & Noise Control', 'High — early sleepers, families with infants and party-goers can split naturally into their own villa', 'Low — single structure means noise carries to all bedrooms'],
                            ['Corporate Retreat Suitability', 'Ideal — separate villas for exec/team sub-groups or male/female split accommodation', 'Basic — common quarters with zero team sub-division'],
                          ].map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-[#FBFBFC]' : 'bg-white'}>
                              <td className="px-4 py-3 align-top text-[#1A1A2E] font-semibold text-xs sm:text-sm">{row[0]}</td>
                              <td className="px-4 py-3 align-top text-[#374151] text-xs sm:text-sm border-l-2 border-[#F57C20]/30">{row[1]}</td>
                              <td className="px-4 py-3 align-top text-[#6B7280] text-xs sm:text-sm">{row[2]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-[#9CA3AF] text-[11px] mt-2 italic">
                        Kitchens at Royal Garden Villas are equipped for meal serving, plating, warming and beverage prep. Full-fledged cooking is not permitted on-site &mdash; guests typically bring catered meals or hire a private cook / caterer.
                      </p>
                    </div>

                    {/* ===== CELEBRATION-ONLY FAQ ===== */}
                    <h4 className="text-lg text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Corporate &amp; Large-Group FAQ
                    </h4>
                    <div data-testid="celebration-faq" className="space-y-2 mb-4">
                      {[
                        {
                          q: 'Do we get exclusive access to the entire 12BHK Celebration Villa compound?',
                          a: 'Yes, 100%. When you book the Celebration Villa, all three villas (3BHK + 4BHK + 5BHK) within the private compound are reserved exclusively for your group. No other guests will have access to the property.'
                        },
                        {
                          q: 'Are the three villas close to each other?',
                          a: 'Absolutely. All three villas sit right next to each other inside the exact same private compound. Your group can walk freely between the villas while still enjoying separate indoor living spaces whenever needed.'
                        },
                        {
                          q: 'Are the swimming pools private to our group?',
                          a: 'Yes. Because the entire compound is booked exclusively for you, all three swimming pools are private to your group with no shared amenities with outsiders.'
                        },
                        {
                          q: 'Can we designate pools for specific groups (e.g. ladies-only or kids)?',
                          a: 'Yes — this is one of the biggest advantages over a standard 12BHK. With 3 separate pools you can dedicate one for ladies, one for kids & families, and one as the main party / entertainment pool.'
                        },
                        {
                          q: 'How do the kitchen facilities work across the compound?',
                          a: 'Each of the three villas has its own fully-equipped kitchen with a complete crockery, cutlery and cooking-utensil set — ideal for warming, serving, plating and beverage prep. This eliminates the long wait times of single-kitchen 12BHKs.'
                        },
                        {
                          q: 'Can we run separate Jain / Pure-Veg meal serving alongside non-veg?',
                          a: 'Yes. With three independent kitchens you can dedicate one exclusively to Jain / Pure-Veg meal serving and plating while the others run non-veg service in parallel — no cross-contamination, no queues.'
                        },
                        {
                          q: 'How does bedtime management work for mixed groups (kids + late-night partygoers)?',
                          a: 'Unlike a single-structure 12BHK where noise carries across all bedrooms, our 3-villa setup gives natural sound isolation. Families with infants or early sleepers can retire to one villa while the rest of the group continues socialising in another villa or on the lawn.'
                        },
                        {
                          q: 'What indoor and outdoor games are provided?',
                          a: 'Three separate sets of indoor & outdoor games — including 3 group swings across 3 private lawns, and multiple carrom boards & board games. Everyone gets to play without having to wait in line.'
                        },
                        {
                          q: 'Is the Celebration Villa suitable for corporate retreats and team offsites?',
                          a: 'Ideal for it. Three villas under one lease means senior management, team leads and team members can each have dedicated private villas for sleeping and getting ready, while coming together on the expansive lawns and pool decks for team-building sessions and evening celebrations.'
                        }
                      ].map((f, i) => (
                        <details key={i} className="bg-white border border-gray-100 rounded-xl group" data-testid={`celebration-faq-item-${i}`}>
                          <summary className="cursor-pointer px-5 py-3 flex items-center justify-between text-[#1A1A2E] text-sm font-medium hover:bg-[#F7F8FA]">
                            <span className="pr-4">{f.q}</span>
                            <FiChevronDown className="w-4 h-4 text-[#9CA3AF] group-open:rotate-180 transition-transform flex-shrink-0" />
                          </summary>
                          <div className="px-5 pb-4">
                            <p className="text-[#6B7280] text-sm leading-relaxed">{f.a}</p>
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                )}
                {/* ===== END CELEBRATION SECTION ===== */}

                {/* House Rules */}
                <h3 className="text-xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>House Rules</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                  {[
                    "Check-in: 1:00 PM | Check-out: 10:00 AM",
                    "Loud music off by 10:00 PM",
                    "Swimming pool: use at own risk",
                    "Kids must be supervised at all times",
                    "No drugs or illegal substances",
                    "Report damages immediately",
                    "Pets not allowed",
                    "Government photo ID required for all guests",
                  ].map((rule, i) => (
                    <div key={i} className="flex items-start gap-2.5 py-1.5">
                      <FiCheck className="w-3.5 h-3.5 text-[#9CA3AF] mt-0.5 flex-shrink-0" />
                      <span className="text-[#6B7280] text-xs">{rule}</span>
                    </div>
                  ))}
                </div>

                {/* Extra Butler Service Info */}
                <div className="bg-[#F57C20]/5 border border-[#F57C20]/20 p-5 rounded-xl mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <FiStar className="w-4 h-4 text-[#F57C20]" />
                    <p className="text-[#1A1A2E] text-sm font-semibold">Special Extra Butler Service Available (Optional)</p>
                  </div>
                  <p className="text-[#6B7280] text-xs leading-relaxed">
                    Opt for a dedicated maid (1 PM - 9 PM) to serve lunch, high tea & dinner at Rs.2,000/day.
                    These charges are for serving only and do not include cooking or supply of food/ingredients.
                    You can add this option during booking.
                  </p>
                  <p className="text-[#1A1A2E] text-[11px] leading-relaxed mt-2.5 pt-2.5 border-t border-[#F57C20]/15">
                    <span className="font-semibold text-[#F57C20]">Please note:</span> General housekeeping is already included in your villa charges (<strong>staff available 10 AM – 6 PM only</strong>). This additional Extra Butler Service is purely optional &mdash; opt in only if you want a dedicated helping-hand exclusively for your group.
                  </p>
                </div>

                {VILLA_PERFECT_FOR[villa.id] && (
                  <div data-testid="villa-perfect-for" className="mb-8 bg-white border border-gray-100 rounded-xl p-5">
                    <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#F57C20] mb-3">Perfect for</p>
                    <div className="flex flex-wrap gap-2">
                      {VILLA_PERFECT_FOR[villa.id].map((s) => (
                        <Link key={s} to={`/${s}`} data-testid={`villa-perfect-for-${s}`} className="border border-gray-200 hover:border-[#F57C20] hover:text-[#F57C20] rounded-full px-4 py-2 text-sm text-[#374151] transition-colors">{landingLabel(s)}</Link>
                      ))}
                    </div>
                  </div>
                )}
                {/* ===================== VILLA-SPECIFIC FAQ (emits FAQPage JSON-LD schema for AI search) ===================== */}
                {(() => {
                  const villaFaqMap = {
                    '3bhk-garden-escape-lonavala': villa3bhkFAQs,
                    '4bhk-garden-retreat-lonavala': villa4bhkFAQs,
                    '5bhk-garden-delight-lonavala': villa5bhkFAQs,
                    '7bhk-harmony-villa-lonavala': villa7bhkFAQs,
                    '8bhk-grandeur-villa-lonavala': villa8bhkFAQs,
                    '9bhk-paradise-villa-lonavala': villa9bhkFAQs,
                    '12bhk-celebration-villa-lonavala': villa12bhkGeneralFAQs,
                  };
                  const faqItems = villaFaqMap[villa.id] || [];
                  if (!faqItems.length) return null;
                  return (
                    <FAQSection
                      title={`${villa.short_name || villa.name} — Frequently Asked Questions`}
                      testid={`villa-${villa.bedrooms}bhk-faq`}
                      compact
                      className="mb-8"
                      items={faqItems}
                    />
                  );
                })()}
              </div>
            )}

            {/* Highlights */}
            {activeTab === 'Highlights' && (
              <div data-testid="tab-highlights-content">
                <h2 className="text-2xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Property Highlights</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: FiDroplet, title: "Private Swimming Pool", desc: "Exclusive pool for your party's use only" },
                    { icon: FiHome, title: `${villa.bedrooms} Spacious Bedrooms`, desc: "Split AC in all bedrooms & living area, with wall/tower fans" },
                    { icon: FiWifi, title: "Complimentary Free High Speed WiFi", desc: "Reliable internet across all rooms" },
                    { icon: FiUsers, title: `Up to ${villa.capacity} Guests`, desc: `Base capacity: ${villa.base_capacity}. Extra: Rs.1,000/night` },
                    { icon: FiStar, title: "Extra Butler Service (Optional)", desc: "Rs.2,000/day for serving meals" },
                    { icon: FiShield, title: "Housekeeping & 24/7 Security", desc: "Housekeeping service available between 10 AM to 6 PM & 24/7 security available" },
                    { icon: FiClock, title: "Early/Late Checkout", desc: "Available at Rs.2,000 extra (subject to availability)" },
                    { icon: FiPercent, title: "Stay Longer, Pay Less", desc: `${nightDiscountPct}% off on every night from the 2nd onwards!` },
                  ].filter((item) => nightDiscountEnabled || item.title !== 'Stay Longer, Pay Less').map((item, i) => (
                    <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#F57C20]/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-[#F57C20]" />
                      </div>
                      <div>
                        <p className="text-[#1A1A2E] text-sm font-semibold mb-0.5">{item.title}</p>
                        <p className="text-[#6B7280] text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {activeTab === 'Amenities' && (
              <div data-testid="tab-amenities-content">
                <h2 className="text-2xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Amenities & Facilities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {villa.amenities?.map((amenity, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-100">
                      <FiCheck className="w-4 h-4 text-[#F57C20] flex-shrink-0" />
                      <span className="text-[#6B7280] text-sm">{amenity}</span>
                    </div>
                  ))}
                  {/* Cooking Space — optional add-on amenity. Shown with a NEW badge + tooltip. */}
                  <div
                    data-testid="villa-amenity-cooking"
                    className="relative flex items-center gap-3 bg-[#F57C20]/5 border border-[#F57C20]/30 rounded-lg p-3 group cursor-help"
                    title="Want home-cooked meals or bringing a personal Maharaj? Access our dedicated kitchen space OUTSIDE the villa for an additional fee. Gas cooking is NOT allowed inside the villa — each villa has an induction stove & microwave only. Guests may also order from restaurants / cloud kitchens / Swiggy / Zomato (delivery to the property)."
                  >
                    <span className="absolute -top-2 -right-2 text-[8px] font-bold tracking-widest bg-[#F57C20] text-white px-1.5 py-0.5 rounded-full animate-pulse">
                      NEW
                    </span>
                    <FiCheck className="w-4 h-4 text-[#F57C20] flex-shrink-0" />
                    <span className="text-[#1A1A2E] text-sm font-semibold">
                      Dedicated Cooking Space <span className="text-[#6B7280] font-normal">(Add-on)</span>
                    </span>
                  </div>
                </div>
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="villa-cooking-note">
                  <p className="text-[11px] text-amber-900 leading-relaxed">
                    <strong>Dedicated Cooking Space (Add-on):</strong> bring your own chef / <em>maharaj</em>.
                    We provide the space, utensils, cooking gas, steel platform &amp; washing sink.
                    Charged separately at booking (Rs.3,000 / day for 3/4/5 BHK · Rs.4,500 / day for 7/8/9 BHK · Rs.7,500 / day for 12 BHK).
                  </p>
                  <p className="text-[11px] text-amber-900/80 leading-relaxed mt-2">
                    <strong>Note:</strong> Each villa has its own kitchen platform &amp; crockery with
                    induction stove &amp; heating microwave. <strong>Gas cooking is NOT allowed inside the villa premises</strong> —
                    food may be ordered &amp; paid directly to restaurants / cloud kitchens / caterers / Swiggy / Zomato
                    (home-delivery available at the property). Gas cooking is permitted ONLY in the optional
                    pre-dedicated cooking space outside the villa, if availed &amp; paid in the booking.
                  </p>
                </div>
                {villa.ideal_for && (
                  <>
                    <h3 className="text-xl text-[#1A1A2E] mt-8 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Ideal For</h3>
                    <div className="flex flex-wrap gap-2">
                      {villa.ideal_for.map((item, i) => (
                        <span key={i} className="text-xs text-[#F57C20] border border-[#F57C20]/20 px-4 py-2 rounded-full bg-[#F57C20]/5 font-medium">{item}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Refund Policy */}
            {activeTab === 'Refund Policy' && (
              <div data-testid="tab-refund-content">
                <h2 className="text-2xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Refund & Cancellation Policy</h2>
                <div className="space-y-4">
                  {[
                    { title: "7+ days before check-in", desc: "Full refund of booking amount (excluding processing fees). Security deposit fully refunded.", color: "text-green-600", bg: "bg-green-50" },
                    { title: "Less than 7 days before check-in", desc: "No refund on booking amount. Security deposit fully refunded.", color: "text-orange-600", bg: "bg-orange-50" },
                    { title: "No-show", desc: "No refund on booking amount. Security deposit refunded after property inspection.", color: "text-red-600", bg: "bg-red-50" },
                  ].map((item, i) => (
                    <div key={i} className={`${item.bg} rounded-xl p-5 border border-gray-100`}>
                      <p className={`${item.color} text-sm font-semibold mb-1`}>{item.title}</p>
                      <p className="text-[#6B7280] text-xs">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 text-[#6B7280] text-xs space-y-2">
                  <p><strong className="text-[#1A1A2E]">Security Deposit:</strong> Refundable within 1 working day post checkout, subject to property inspection.</p>
                  <p><strong className="text-[#1A1A2E]">Extra Butler Service (Optional):</strong> Cancellable 24 hours before. Same-day cancellations non-refundable.</p>
                  <p><strong className="text-[#1A1A2E]">Early Departure:</strong> No refund for unused nights.</p>
                </div>
                <div className="mt-6">
                  <Link to="/refund-policy" className="text-[#F57C20] text-sm font-semibold hover:underline">View Full Refund Policy &rarr;</Link>
                </div>
              </div>
            )}

            {/* Location */}
            {activeTab === 'Location' && (
              <div data-testid="tab-location-content">
                <h2 className="text-2xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Location</h2>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-6">
                  Royal Garden Villas, Lonavala, Pune District, Maharashtra 410401. Nestled in the Western Ghats with
                  stunning valley views.
                </p>
                <div className="space-y-3 mb-6">
                  {[
                    "83 km from Mumbai (2 hours via Expressway)",
                    "64 km from Pune (1.5 hours drive)",
                    "Nearest railway station: Lonavala (5 km)",
                    "Nearest airport: Pune (70 km) or Mumbai (95 km)",
                    "Private gated community, 24/7 security",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <FiMapPin className="w-4 h-4 text-[#F57C20] mt-0.5 flex-shrink-0" />
                      <span className="text-[#6B7280] text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <h3 className="text-lg text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Nearby Attractions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { name: "Tiger Point", dist: "5 km" }, { name: "Bhushi Dam", dist: "4 km" },
                    { name: "Lonavala Lake", dist: "3 km" }, { name: "Karla Caves", dist: "12 km" },
                    { name: "Mapro Garden", dist: "8 km" }, { name: "Imagica Theme Park", dist: "15 km" },
                  ].map((p, i) => (
                    <div key={i} className="flex justify-between bg-white p-3 rounded-lg border border-gray-100 text-sm">
                      <span className="text-[#1A1A2E] font-medium">{p.name}</span>
                      <span className="text-[#F57C20] text-xs font-semibold">{p.dist}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ===== SIDEBAR (Sticky Pricing) ===== */}
          <div className="lg:col-span-1">
            <div className="sticky top-[180px] space-y-5">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-lg">
                <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-4">Pricing</p>
                <div className="space-y-3 mb-5">
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                    <span className="text-[#6B7280] text-sm">Weekday</span>
                    <span className="text-[#1A1A2E] text-lg font-bold">Rs.{villa.base_price?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                    <span className="text-[#6B7280] text-sm">Weekend</span>
                    <span className="text-[#1A1A2E] text-lg font-bold">Rs.{villa.weekend_price?.toLocaleString()}</span>
                  </div>
                  {nightDiscountEnabled && <>
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                    <span className="text-[#6B7280] text-sm">1st Night</span>
                    <span className="text-[#1A1A2E] font-medium text-sm">Full Price</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                    <span className="text-[#6B7280] text-sm">2nd Night onwards</span>
                    <span className="text-[#F57C20] font-bold text-sm">{nightDiscountPct}% OFF each</span>
                  </div>
                  </>}
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                    <span className="text-[#6B7280] text-sm">Deposit</span>
                    <span className="text-[#1A1A2E] text-sm font-medium">Rs.{villa.security_deposit?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
                    <span className="text-[#6B7280] text-sm">Extra Guest</span>
                    <span className="text-[#1A1A2E] text-sm font-medium">Rs.1,000/person/night</span>
                  </div>
                  {/* Optional Add-ons - Collapsible */}
                  {(() => {
                    const items = [
                      { label: 'Extra Butler Service', price: 'Rs.2,000/day', testid: 'sidebar-addon-butler' },
                      { label: 'BBQ Service', price: 'Rs.3,000/meal', testid: 'sidebar-addon-bbq' },
                      { label: 'Bonfire', price: 'Rs.2,000/session', testid: 'sidebar-addon-bonfire' },
                    ];
                    if (cookingEnabled) {
                      const perDay = cookingPricingMap[villa.id];
                      if (perDay) {
                        items.push({
                          label: 'Dedicated Cooking Space',
                          price: `Rs.${perDay.toLocaleString()}/day`,
                          testid: 'sidebar-addon-cooking',
                          isNew: true,
                        });
                      }
                    }
                    return items.map((item, i) => (
                      <details key={item.label} className="py-2.5 border-b border-gray-100 group" data-testid={item.testid}>
                        <summary className="flex justify-between items-center cursor-pointer list-none">
                          <span className="text-[#6B7280] text-sm flex items-center gap-1.5">
                            {item.label} <span className="text-[#F57C20] text-[10px]">(Optional)</span>
                            {item.isNew && <span className="text-[8px] font-bold bg-[#F57C20] text-white px-1.5 py-0.5 rounded-full tracking-wider animate-pulse">NEW</span>}
                          </span>
                          <FiChevronDown className="w-4 h-4 text-[#9CA3AF] group-open:rotate-180 transition-transform" />
                        </summary>
                        <div className="mt-2 text-right">
                          <span className="text-[#1A1A2E] text-sm font-medium">{item.price}</span>
                        </div>
                      </details>
                    ));
                  })()}
                </div>

                <Link to={`/booking?villa=${villa.id}`} data-testid="villa-sidebar-book-btn" className="block w-full bg-[#F57C20] text-white py-4 text-center text-sm font-semibold tracking-wide rounded-lg hover:bg-[#E06A10] transition-all hover:shadow-lg hover:shadow-orange-500/20 mb-3">
                  Book This Villa
                </Link>
                <a href="https://wa.me/919820088292" target="_blank" rel="noopener noreferrer" className="block w-full border-2 border-[#F57C20] text-[#F57C20] py-3 text-center text-sm font-semibold rounded-lg hover:bg-[#F57C20] hover:text-white transition-all">
                  WhatsApp Enquiry
                </a>
                <p className="text-[#9CA3AF] text-xs text-center mt-3">Check-in: 1 PM &middot; Check-out: 10 AM</p>
              </div>

              {/* Discount Box */}
              {nightDiscountEnabled && <div className="bg-[#F57C20]/5 border border-[#F57C20]/20 p-5 rounded-xl">
                <p className="text-[#F57C20] text-sm font-semibold mb-2 flex items-center gap-1.5"><FiPercent className="w-4 h-4" /> Stay Longer, Pay Less</p>
                <p className="text-[#6B7280] text-xs">
                  1st night at actual price. <span className="text-[#F57C20] font-semibold">{nightDiscountPct}% off</span> on every night from the 2nd onwards.
                </p>
              </div>}

              {/* Extra Butler Service */}
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <p className="text-[#1A1A2E] text-sm font-semibold mb-2 flex items-center gap-1.5"><FiStar className="w-4 h-4 text-[#F57C20]" /> Extra Butler Service (Optional)</p>
                <p className="text-[#6B7280] text-xs">Rs.2,000/day (1 PM - 9 PM). Serving lunch, high tea & dinner. Charges for serving only, not cooking/supply. Add during booking.</p>
                <p className="text-[#1A1A2E] text-[10px] leading-relaxed mt-2.5 pt-2.5 border-t border-gray-100">
                  <span className="font-semibold text-[#F57C20]">Please note:</span> General housekeeping is already included in your villa charges (<strong>staff available 10 AM – 6 PM only</strong>). This additional Maid Service is purely optional &mdash; opt in only if you want a dedicated helping-hand exclusively for your group.
                </p>
              </div>

              {/* BBQ Service */}
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <p className="text-[#1A1A2E] text-sm font-semibold mb-2 flex items-center gap-1.5"><FiStar className="w-4 h-4 text-[#F57C20]" /> BBQ Service (Optional)</p>
                <p className="text-[#6B7280] text-xs mb-1">Rs.3,000/meal (1 labour included). Extra labour +Rs.1,000/person/meal. Includes Sigdi, Metal Sticks, Charcoal & Labour for 2 hours.</p>
                <p className="text-[#F57C20]/70 text-[10px] italic">Food not included. Max 2 meals/day.</p>
              </div>

              {/* Bonfire Service */}
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <p className="text-[#1A1A2E] text-sm font-semibold mb-2 flex items-center gap-1.5"><FiStar className="w-4 h-4 text-[#F57C20]" /> Bonfire Service (Optional)</p>
                <p className="text-[#6B7280] text-xs">Rs.2,000/session. Includes Sigdi and wooden sticks for a cozy bonfire experience.</p>
              </div>

              {/* Food Note */}
              <div className="bg-[#FFF8F0] rounded-xl p-5 border border-[#F57C20]/15">
                <p className="text-[#1A1A2E] text-sm font-semibold mb-2">Food Ordering</p>
                <p className="text-[#6B7280] text-xs leading-relaxed">Royal Garden Villas offers facility of direct ordering of Food by Guests (No Monopoly) - to be ordered & paid directly by guests to Caterers / Cloud Kitchens, who take orders on KG basis and give free home delivery. Swiggy & Zomato also works. Kitchen equipped with Crockery, Induction and Microwave. Contact details of Caterers & Restaurants available at Villa.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== GALLERY GRID ===== */}
      {images.length > 3 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <h3 className="text-2xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Photo Gallery</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {images.slice(0, 8).map((img, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden rounded-xl cursor-pointer group" onClick={() => setShowAllPhotos(true)}>
                <PictureImage src={img} alt={`${villa.name} photo ${i + 1}`}
                  width={600} height={450}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ===== EXPLORE MORE ===== */}
      {allVillas.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex justify-between items-end mb-8">
              <div>
                <p className="label-uppercase mb-2">More Options</p>
                <h3 className="text-2xl text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Explore More <span className="text-[#F57C20] italic">Villas</span>
                </h3>
              </div>
              <Link to="/villas" className="hidden md:flex items-center gap-2 text-[#F57C20] text-sm font-semibold hover:gap-3 transition-all">View All <FiArrowRight /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {allVillas.slice(0, 3).map(v => (
                <Link key={v.id} to={`/villas/${v.id}`} data-testid={`explore-villa-${v.id}`} className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <PictureImage src={v.images?.[0]} alt={`${v.name}`}
                      width={640} height={400}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    {nightDiscountEnabled && (
                      <div className="absolute top-3 right-3 bg-[#F57C20] text-white text-[9px] font-bold px-2.5 py-1 rounded">{nightDiscountPct}% OFF 2nd Night+</div>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-[#F57C20] text-xs font-semibold tracking-wider uppercase mb-1">{v.bedrooms}BHK &middot; {v.capacity} guests</p>
                    <h4 className="text-lg font-bold text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>{withBhk(v, v.short_name || v.name)}</h4>
                    <p className="text-[#6B7280] text-sm mt-1">From Rs.{v.weekend_price?.toLocaleString()} / night</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== MOBILE STICKY BOOK BAR ===== */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-2xl" data-testid="mobile-book-bar">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[#1A1A2E] text-lg font-bold">Rs.{villa.weekend_price?.toLocaleString()}<span className="text-xs font-normal text-[#6B7280]"> /night</span></p>
            {nightDiscountEnabled && (
              <p className="text-[#F57C20] text-[10px] font-semibold">{nightDiscountPct}% OFF 2nd Night Onwards</p>
            )}
          </div>
          <Link to={`/booking?villa=${villa?.id || ''}`} className="bg-[#F57C20] text-white px-6 py-3 text-sm font-semibold rounded-lg hover:bg-[#E06A10] transition-all">
            Book Now
          </Link>
        </div>
      </div>

      {/* ===== FULL PHOTO MODAL ===== */}
      {showAllPhotos && (
        <div className="fixed inset-0 z-[80] bg-black/95 overflow-y-auto" onClick={() => setShowAllPhotos(false)}>
          <div className="max-w-5xl mx-auto px-4 py-8" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white text-xl font-semibold">{villa.name} - All Photos</h3>
              <button onClick={() => setShowAllPhotos(false)} className="text-white/60 hover:text-white text-sm font-medium px-4 py-2 border border-white/20 rounded-lg">Close</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {images.map((img, i) => (
                <div key={i} className="aspect-[16/10] overflow-hidden rounded-xl">
                  <PictureImage src={img} alt={`${villa.name} ${i + 1}`}
                    width={1200} height={750}
                    className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VillaDetail;
